<template>
  <div class="app-shell home-page tool-page">
    <div class="app-shell__main">
      <AppHeader />

      <main class="home-main tool-main">
        <section class="hero-section tool-hero-section tool-page-hero">
          <div class="tool-hero-grid">
            <div class="tool-hero-copy">
              <span class="tool-eyebrow">Video Edit</span>
              <h1 class="hero-title">视频去水印</h1>
              <p class="hero-description">上传本地视频并框选区域，快速去除画面中的水印元素。</p>
              <div class="tool-copy-pills">
                <span>框选区域后再开始处理</span>
                <span>适合本地剪辑和素材整理</span>
                <span>处理后可直接保存成片</span>
              </div>
              <div class="tool-hero-summary">
                <article class="tool-hero-stat">
                  <strong>本地视频直接上传</strong>
                  <span>支持拖入本地素材，不需要额外转换格式后再处理。</span>
                </article>
                <article class="tool-hero-stat">
                  <strong>按画面区域框选</strong>
                  <span>只针对选中的水印位置处理，交互更直观，也更适合预览确认。</span>
                </article>
                <article class="tool-hero-stat">
                  <strong>更适合剪辑前整理</strong>
                  <span>适合做本地二次创作、素材归档和常规视频清理。</span>
                </article>
              </div>
            </div>

            <div class="tool-workbench tool-workbench--link">
              <!-- <div class="tool-stage-note">
                <div class="tool-stage-note__title">视频处理工作台</div>
                <p>上传视频后框选需要移除的区域，确认范围无误再开始处理，更适合先看后做。</p>
              </div> -->
              <div class="video-editor-container">
                <div
                  v-if="!videoUrl"
                  class="upload-area"
                  :class="{ 'is-drag-active': isDragActive }"
                  @click="triggerUpload"
                  @dragenter.prevent="handleDragEnter"
                  @dragover.prevent="handleDragOver"
                  @dragleave.prevent="handleDragLeave"
                  @drop.prevent="handleDrop"
                >
                  <input type="file" ref="fileInput" accept="video/*" class="hidden-input" @change="handleFileChange" />
                  <div class="upload-placeholder">
                    <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <span class="upload-text">点击上传视频</span>
                    <span class="upload-hint">支持 MP4 / MOV 等格式，也可直接拖入视频，建议 5 分钟以内</span>
                  </div>
                </div>

                <div v-else class="editor-area">
                  <div class="canvas-wrapper" ref="videoWrapper" :style="containerHeight ? { height: containerHeight + 'px' } : { minHeight: '320px' }" @contextmenu.prevent>
                    <video
                      ref="videoRef"
                      :src="videoUrl"
                      class="editor-image"
                      controls
                      controlsList="nodownload"
                      disablePictureInPicture
                      crossorigin="anonymous"
                      @loadedmetadata="onVideoLoaded"
                    ></video>

                    <div
                      v-if="isEditing"
                      class="mask-box"
                      :style="{ left: videoOffsetX + box.x + 'px', top: videoOffsetY + box.y + 'px', width: box.width + 'px', height: box.height + 'px' }"
                      @mousedown.stop="startDrag"
                      @touchstart.stop="startDrag"
                    >
                      <div class="mask-box-border"></div>
                      <div class="resize-handle handle-tl" @mousedown.stop.prevent="startResize('tl', $event)" @touchstart.stop.prevent="startResize('tl', $event)"></div>
                      <div class="resize-handle handle-tr" @mousedown.stop.prevent="startResize('tr', $event)" @touchstart.stop.prevent="startResize('tr', $event)"></div>
                      <div class="resize-handle handle-bl" @mousedown.stop.prevent="startResize('bl', $event)" @touchstart.stop.prevent="startResize('bl', $event)"></div>
                      <div class="resize-handle handle-br" @mousedown.stop.prevent="startResize('br', $event)" @touchstart.stop.prevent="startResize('br', $event)"></div>
                    </div>
                  </div>

                  <div class="toolbar">
                    <div class="tool-group">
                      <button class="tool-btn" @click="toggleEdit" :class="{ active: isEditing }">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                          <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                          <path d="M2 2l7.586 7.586"></path>
                          <circle cx="11" cy="11" r="2"></circle>
                        </svg>
                        <span>{{ isEditing ? '取消框选' : '框选水印' }}</span>
                      </button>
                    </div>

                    <div class="divider"></div>

                    <div class="tool-group">
                      <button class="tool-btn" @click="undo" :disabled="historyStep <= 0">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M3 7v6h6"></path>
                          <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
                        </svg>
                        <span>撤销</span>
                      </button>
                      <button class="tool-btn" @click="redo" :disabled="historyStep >= history.length - 1">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M21 7v6h-6"></path>
                          <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"></path>
                        </svg>
                        <span>恢复</span>
                      </button>
                    </div>

                    <div class="divider"></div>

                    <div class="tool-group action-group">
                      <button class="tool-btn secondary" @click="resetVideo" :disabled="isProcessing || !hasBox">重选视频</button>
                      <button class="tool-btn primary" @click="removeWatermark" :disabled="isProcessing || !hasBox">开始去除</button>
                      <button class="tool-btn success" @click="saveVideoResult(0)" :disabled="!processedUrl">保存视频</button>
                    </div>
                  </div>
                </div>

                <p class="hint-text">点击“框选水印”调整选区，覆盖水印后点击“开始去除”。</p>
              </div>
            </div>

            <div class="learn-more">
              <a href="#how-it-works" class="learn-more-link">
                <div class="learn-more-text">了解更多</div>
                <svg class="arrow-down" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </a>
            </div>
          </div>
        </section>

        <section id="how-it-works" class="how-it-works-section tool-info-section">
          <div class="container">
            <div class="tool-section-head">
              <h2 class="section-title">如何使用</h2>
              <p>上传视频、调整选区、开始处理，工作流保持和首页一样直接。</p>
            </div>
            <div class="steps-grid">
              <div class="step-card">
                <div class="step-number">1</div>
                <h3 class="step-title">上传视频</h3>
                <p class="step-description">选择需要处理的视频文件。</p>
              </div>
              <div class="step-card">
                <div class="step-number">2</div>
                <h3 class="step-title">框选水印</h3>
                <p class="step-description">在预览区调整去除范围。</p>
              </div>
              <div class="step-card">
                <div class="step-number">3</div>
                <h3 class="step-title">一键去除</h3>
                <p class="step-description">处理完成后直接保存结果。</p>
              </div>
            </div>
          </div>
        </section>

        <section id="features" class="features-section tool-info-section">
          <div class="container">
            <div class="tool-section-head">
              <h2 class="section-title">功能特点</h2>
              <p>围绕本地视频处理场景，用更短的表达把重点说清楚。</p>
            </div>
            <div class="features-grid">
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </div>
                <h3 class="feature-title">智能识别</h3>
                <p class="feature-description">针对局部区域进行稳定处理。</p>
              </div>
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 2.69l5.74 5.74-5.74 5.74-5.74-5.74L12 2.69M12 22a9.97 9.97 0 0 1-7.07-2.93l2.83-2.83A5.97 5.97 0 0 0 12 18c3.31 0 6-2.69 6-6h4c0 5.52-4.48 10-10 10z"></path>
                  </svg>
                </div>
                <h3 class="feature-title">极速处理</h3>
                <p class="feature-description">减少等待时间，更快得到结果。</p>
              </div>
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M23 7l-7 5 7 5V7z"></path>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                  </svg>
                </div>
                <h3 class="feature-title">保持画质</h3>
                <p class="feature-description">尽量保留原视频清晰度与比例。</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import request from '@/utils/request'
import { getRemoveKey } from '@/utils/crypto'
import OSS from '@/utils/lib/aliyun-oss-sdk-6.17.1.min.js'
window.OSS = OSS;
import '@/utils/aliyun-upload-sdk-1.5.7.min.js'
import { showDialog, showConfirmDialog, showToast, showLoadingToast, closeToast } from 'vant'

export default {
  name: 'VideoRemove',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      videoUrl: null,
      processedUrl: null,
      videoWidth: 0,
      videoHeight: 0,
      containerWidth: 0,
      containerHeight: 0,
      videoOffsetX: 0,
      videoOffsetY: 0,
      
      // Box State
      isEditing: false,
      box: {
        x: 50,
        y: 50,
        width: 100,
        height: 50
      },
      hasBox: false,
      
      // Drag/Resize State
      isDragging: false,
      isResizing: false,
      resizeEdge: null,
      startX: 0,
      startY: 0,
      initialBox: null,
      
      // History
      history: [],
      historyStep: -1,
      
      // Loading
      isUploading: false,
      isProcessing: false,
      isDragActive: false,
      dragDepth: 0,
      
      // Download State
      downloadingItems: new Set(),
      downloadProgress: {},
      
      // VOD
      uploader: null,
      vodUrl: "https://vod-sz.adaiyun.net/",
    }
  },
  mounted() {
    this.initVod()
    // Global event listeners for drag/resize
    window.addEventListener('mousemove', this.handleMove)
    window.addEventListener('mouseup', this.handleEnd)
    window.addEventListener('touchmove', this.handleMove, { passive: false })
    window.addEventListener('touchend', this.handleEnd)
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.handleMove)
    window.removeEventListener('mouseup', this.handleEnd)
    window.removeEventListener('touchmove', this.handleMove)
    window.removeEventListener('touchend', this.handleEnd)
  },
  methods: {
    initVod() {
      const that = this
      if (!window.AliyunUpload || !window.AliyunUpload.Vod) {
        console.error('AliyunUpload SDK not loaded')
        return
      }
      this.uploader = new window.AliyunUpload.Vod({
        userId: "1048463214720895",
        region: "cn-shenzhen",
        partSize: 1048576,
        parallel: 5,
        retryCount: 3,
        retryDuration: 2,
        onUploadstarted: async function (uploadInfo) {
          console.log('onUploadstarted', uploadInfo)
          let url = "/aliyun/vod/create/upload-video-auth" // Reuse auth for now, or check if video specific needed
          let data = {
            title: uploadInfo.file.name,
            filename: that.getFilename(uploadInfo.url),
            tags: 'syf'
          }

          try {
            const res = await request(url, {
              method: 'POST',
              body: data
            })

            if (res) {
              const { uploadAuth, uploadAddress, videoId } = res
              that.uploader.setUploadAuthAndAddress(uploadInfo, uploadAuth, uploadAddress, videoId)
            } else {
              that.isUploading = false
              showToast('获取上传凭证失败')
            }
          } catch (err) {
            console.error('Error getting upload auth', err)
            that.isUploading = false
            showToast('上传失败')
          }
        },
        onUploadSucceed: function (uploadInfo) {
          console.log('onUploadSucceed', uploadInfo)
          const url = that.vodUrl + uploadInfo.object
          that.videoUrl = url
          that.processedUrl = url
          that.pushHistory(url)
          that.isUploading = false
          closeToast()
          showToast('上传成功')
        },
        onUploadFailed: function (uploadInfo, code, message) {
          console.error('onUploadFailed', uploadInfo, code, message)
          that.isUploading = false
          closeToast()
          showToast('上传失败: ' + message)
        },
        onUploadProgress: function (uploadInfo, totalSize, loadedPercent) {
          console.log('onUploadProgress', uploadInfo, totalSize, loadedPercent)
        },
        onUploadTokenExpired: function (uploadInfo) {
          console.log('onUploadTokenExpired', uploadInfo)
        }
      })
    },

    triggerUpload() {
      const token = localStorage.getItem('token')
      if (!token) {
        console.log('token失效或不可用')
        window.dispatchEvent(new CustomEvent('auth-login-required', { detail: { msg: '登录失效或不可用，请重新登录' } }))
        return
      }
      this.$refs.fileInput.click()
    },
    getFilename(url) {
      if (!url || typeof url !== 'string') {
          console.error('url is null')
          return 'default.mp4'
      }
      // 移除查询参数和哈希
      const cleanUrl = url.split('?')[0].split('#')[0]
      
      // 处理Windows路径
      const normalizedUrl = cleanUrl.replace(/\\/g, '/')
      
      const pos = normalizedUrl.lastIndexOf('/')
      if (pos === -1) {
        return normalizedUrl
      }
      
      const filename = normalizedUrl.substring(pos + 1)
      return filename || ''
    },

    handleFileChange(e) {
      const file = e.target.files[0]
      e.target.value = ''
      if (!file) return
      this.processSelectedFile(file)
    },

    handleDragEnter() {
      this.dragDepth += 1
      this.isDragActive = true
    },

    handleDragOver(e) {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy'
      }
      this.isDragActive = true
    },

    handleDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) {
        this.isDragActive = false
      }
    },

    handleDrop(e) {
      this.dragDepth = 0
      this.isDragActive = false
      const file = e.dataTransfer?.files?.[0]
      if (!file) return
      this.processSelectedFile(file)
    },

    processSelectedFile(file) {
      if (!file.type.startsWith('video/')) {
        showToast('请拖入视频文件')
        return
      }

      if (file.size > 100 * 1024 * 1024) {
        showToast('视频大小不能超过100MB')
        return
      }

      // Local preview
      this.videoUrl = URL.createObjectURL(file)
      this.history = []
      this.historyStep = -1
      this.hasBox = false
      
      // Upload
      this.isUploading = true
      showLoadingToast({
        message: '视频上传中...',
        forbidClick: true,
        duration: 0
      })
      
      if (this.uploader) {
        this.uploader.addFile(file, null, null, null, '{"Vod":{}}')
        this.uploader.startUpload()
      } else {
        this.isUploading = false
        closeToast()
        showToast('上传组件未就绪')
      }
    },

    onVideoLoaded(e) {
      const video = e.target
      this.videoWidth = video.videoWidth
      this.videoHeight = video.videoHeight
      this.updateContainerSize()
      window.addEventListener('resize', this.updateContainerSize)
      
      // Initialize box in center
      this.resetBox()
    },
    
    updateContainerSize() {
      if (!this.$refs.videoRef || !this.$refs.videoWrapper) return
      const videoRect = this.$refs.videoRef.getBoundingClientRect()
      const wrapperRect = this.$refs.videoWrapper.getBoundingClientRect()
      this.containerWidth = videoRect.width
      this.containerHeight = videoRect.height
      this.videoOffsetX = Math.max(0, (wrapperRect.width - videoRect.width) / 2)
      this.videoOffsetY = Math.max(0, (wrapperRect.height - videoRect.height) / 2)
    },

    resetBox() {
      this.box = {
        x: this.containerWidth / 2 - 50,
        y: this.containerHeight / 2 - 25,
        width: 100,
        height: 50
      }
      this.hasBox = true
    },

    toggleEdit() {
      this.isEditing = !this.isEditing
      if (this.isEditing && !this.hasBox) {
        this.resetBox()
      }
    },

    // Drag & Resize Logic
    startDrag(e) {
      if (!this.isEditing) return
      this.isDragging = true
      this.startX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
      this.startY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY
      this.initialBox = { ...this.box }
    },

    startResize(edge, e) {
      if (!this.isEditing) return
      this.isResizing = true
      this.resizeEdge = edge
      this.startX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
      this.startY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY
      this.initialBox = { ...this.box }
    },

    handleMove(e) {
      if (!this.isDragging && !this.isResizing) return
      e.preventDefault()
      
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
      const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY
      
      const dx = clientX - this.startX
      const dy = clientY - this.startY
      
      if (this.isDragging) {
        let newX = this.initialBox.x + dx
        let newY = this.initialBox.y + dy
        
        // Boundaries
        newX = Math.max(0, Math.min(newX, this.containerWidth - this.initialBox.width))
        newY = Math.max(0, Math.min(newY, this.containerHeight - this.initialBox.height))
        
        this.box.x = newX
        this.box.y = newY
      } else if (this.isResizing) {
        let newX = this.initialBox.x
        let newY = this.initialBox.y
        let newW = this.initialBox.width
        let newH = this.initialBox.height
        
        const minSize = 20
        
        if (this.resizeEdge.includes('l')) {
          const maxDx = this.initialBox.width - minSize
          const d = Math.min(dx, maxDx)
          newX += d
          newW -= d
        }
        if (this.resizeEdge.includes('r')) {
          newW = Math.max(minSize, this.initialBox.width + dx)
        }
        if (this.resizeEdge.includes('t')) {
          const maxDy = this.initialBox.height - minSize
          const d = Math.min(dy, maxDy)
          newY += d
          newH -= d
        }
        if (this.resizeEdge.includes('b')) {
          newH = Math.max(minSize, this.initialBox.height + dy)
        }
        
        // Boundary check for resize
        if (newX < 0) { newW += newX; newX = 0; }
        if (newY < 0) { newH += newY; newY = 0; }
        if (newX + newW > this.containerWidth) newW = this.containerWidth - newX
        if (newY + newH > this.containerHeight) newH = this.containerHeight - newY
        
        this.box = { x: newX, y: newY, width: newW, height: newH }
      }
    },

    handleEnd() {
      this.isDragging = false
      this.isResizing = false
      this.resizeEdge = null
    },

    resetVideo() {
      this.videoUrl = ''
      this.processedUrl = ''
      this.isDragActive = false
      this.dragDepth = 0
      this.history = []
      this.historyStep = -1
      this.containerWidth = 0
      this.containerHeight = 0
      this.videoOffsetX = 0
      this.videoOffsetY = 0
      this.box = null
      this.hasBox = false
      this.isEditing = false
      this.isProcessing = false
    },

    async removeWatermark() {
      if (this.isProcessing) return
      if (!this.box) return
      
      this.isProcessing = true
      showLoadingToast({
        message: '处理中...',
        forbidClick: true,
        duration: 0
      })

      try {
        // Calculate coordinates relative to actual video size
        const scaleX = this.videoWidth / this.containerWidth
        const scaleY = this.videoHeight / this.containerHeight
        
        const x = Math.round(this.box.x * scaleX)
        const y = Math.round(this.box.y * scaleY)
        const width = Math.round(this.box.width * scaleX)
        const height = Math.round(this.box.height * scaleY)
        
        // 1. Get Key
        const keyRes = await request('/remove-watermark/create-key', { method: 'POST' })
        const key = getRemoveKey(keyRes.data, keyRes.timestamp)
        
        // 2. Call API
        const res = await request('/remove-watermark/local-video', {
          method: 'POST',
          contentType: 'application/json',
          body: {
            x, y, width, height,
            videoUrl: this.videoUrl,
            videoWidth: this.videoWidth,
            videoHeight: this.videoHeight,
            key
          }
        })
        
        if (res) {
          this.videoUrl = res
          this.processedUrl = res
          this.pushHistory(res)
          this.isEditing = false
          showToast('去除成功')
        }
      } catch (error) {
        console.error('Remove failed', error)
        showDialog({ message: error.message || '处理失败' })
      } finally {
        this.isProcessing = false
        closeToast()
      }
    },
    
    pushHistory(url) {
      // Remove future steps if any
      if (this.historyStep < this.history.length - 1) {
        this.history = this.history.slice(0, this.historyStep + 1)
      }
      this.history.push(url)
      this.historyStep = this.history.length - 1
    },
    
    undo() {
      if (this.historyStep > 0) {
        this.historyStep--
        this.videoUrl = this.history[this.historyStep]
        this.processedUrl = this.videoUrl
      }
    },
    
    redo() {
      if (this.historyStep < this.history.length - 1) {
        this.historyStep++
        this.videoUrl = this.history[this.historyStep]
        this.processedUrl = this.videoUrl
      }
    },

    async saveVideoResult(forceUseStatus = 0) {
      if (!this.processedUrl) return
      
      showLoadingToast({
        message: '保存中...',
        forbidClick: true,
        duration: 0
      })

      try {
        const keyRes = await request('/remove-watermark/create-key', { method: 'POST' })
        const key = getRemoveKey(keyRes.data, keyRes.timestamp)
        
        const res = await request('/remove-watermark/local-video/create-one', {
          method: 'POST',
          // contentType: 'application/json',
          body: {
            key,
            videoUrl: this.processedUrl,
            forceUseStatus
          }
        })
        
        closeToast()
        
        showConfirmDialog({
          title: '保存视频',
          message: '是否直接下载？或拷贝链接到浏览器打开',
          confirmButtonText: '直接下载',
          cancelButtonText: '拷贝链接',
        }).then(() => {
          this.downloadVideo(res)
        }).catch((error) => {
          if (error === 'cancel') {
            this.copyToClipboard(res)
          }
        })
        
      } catch (error) {
        closeToast()
        if (error.code == 4010) {
          showConfirmDialog({ message: '积分不足，成为会员后再尝试' })
          .then(() => {
            this.$router.push({ name: 'WebPay'})
          })
          .catch(() => {
            // showDialog({ message: error.message || '保存失败' })
          })
        } else if (error.code == 405) {
          showConfirmDialog({ message: '已达会员每日次数使用上限，是否继续使用积分？' })
          .then(() => {
            this.saveVideoResult(1)
          })
          .catch(() => {
            // showDialog({ message: error.message || '保存失败' })
          })
        } else if (error.code == 406) {
          showConfirmDialog({ message: '已达会员每日100次使用上限且无可用积分，是否需要获取更多积分？' })
          .then(() => {
            // 跳转到积分页面
            this.$router.push({ name: 'WebPay' })
          }).catch(() => {
            // 跳转到积分页面
            // this.$router.push({ name: 'WebPay' })
          })
        } else {
          showDialog({ message: error.message || '保存失败' })
        }
      }
    },
    
    async downloadVideo(url, type = 'video', httpHeaders = null) {
      const downloadKey = `${type}_${url}`
      if (this.downloadingItems.has(downloadKey)) {
        return
      }

      this.downloadingItems.add(downloadKey)
      this.updateDownloadProgress(downloadKey, 0)

      try {
        // 检查是否支持Range请求
        const supportsRange = await this.checkRangeSupport(url, httpHeaders)

        if (supportsRange) {
          console.log('服务器支持Range请求，使用多线程下载')
          await this.downloadVideoWithMultiThread(url, type, 1024 * 1024, httpHeaders)
        } else {
          console.log('服务器不支持Range请求，使用单线程下载')
          await this.downloadVideoSingleThread(url, type, httpHeaders)
        }
      } catch (error) {
        console.error('下载失败:', error)
        this.downloadProgress[downloadKey] = { progress: 0, status: 'error' }
        showToast('下载失败，请尝试直接打开链接')
        
        // 延迟清理
        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 3000)
      }
    },

    // 根据URL获取合适的Referer
    getRefererFromUrl(url) {
      try {
        const urlObj = new URL(url)
        const hostname = urlObj.hostname.toLowerCase()

        if (hostname.includes('douyin') || hostname.includes('tiktok')) {
          return 'https://www.douyin.com/'
        } else if (hostname.includes('kuaishou')) {
          return 'https://www.kuaishou.com/'
        } else if (hostname.includes('xiaohongshu') || hostname.includes('xhscdn')) {
          return 'https://www.xiaohongshu.com/'
        } else if (hostname.includes('instagram') || hostname.includes('cdninstagram')) {
          return 'https://www.instagram.com/'
        } else if (hostname.includes('youtube') || hostname.includes('googlevideo')) {
          return 'https://www.youtube.com/'
        } else if (hostname.includes('twitter') || hostname.includes('twimg')) {
          return 'https://twitter.com/'
        } else {
          return `${urlObj.protocol}//${urlObj.hostname}/`
        }
      } catch (error) {
        console.warn('无法解析URL，使用默认Referer:', error)
        return 'https://www.douyin.com/'
      }
    },

    // 检查服务器是否支持Range请求
    async checkRangeSupport(url, httpHeaders = null) {
      try {
        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const fetchOptions = {
          method: 'HEAD',
          headers: headers,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const response = await fetch(url, fetchOptions)
        return response.headers.get('accept-ranges') === 'bytes'
      } catch {
        return false
      }
    },

    // 更新下载进度
    updateDownloadProgress(downloadKey, progress, speed = null, status = 'downloading') {
      this.downloadProgress[downloadKey] = {
        progress,
        status,
        speed: speed ? `${(speed / 1024 / 1024).toFixed(1)} MB/s` : null
      }
      
      // 显示全局下载进度提示
      if (status === 'downloading') {
        const speedText = speed ? `${(speed / 1024 / 1024).toFixed(1)} MB/s` : ''
        showLoadingToast({
          message: `下载中 ${progress}% ${speedText}`,
          forbidClick: true,
          duration: 0
        })
      } else if (status === 'completed') {
        closeToast()
        showToast('下载完成')
      } else if (status === 'error') {
        closeToast()
        showToast('下载失败')
      }
    },

    // 触发文件下载
    triggerDownload(blob, filename) {
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()

      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      }, 1000)
    },

    // 多线程下载实现
    async downloadVideoWithMultiThread(url, type, chunkSize = 1024 * 1024, httpHeaders = null) {
      const downloadKey = `${type}_${url}`

      try {
        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const headFetchOptions = {
          method: 'HEAD',
          headers: headers,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const headResponse = await fetch(url, headFetchOptions)
        const fileSize = parseInt(headResponse.headers.get('content-length'))

        if (!fileSize || fileSize < chunkSize * 2) {
          return this.downloadVideoSingleThread(url, type, httpHeaders)
        }

        const chunkCount = Math.ceil(fileSize / chunkSize)
        const maxConcurrent = Math.min(chunkCount, 6)

        this.updateDownloadProgress(downloadKey, 0, null, 'downloading')

        const chunks = new Array(chunkCount)
        const downloadedChunks = new Set()
        const startTime = Date.now()
        let downloadedBytes = 0

        for (let batchStart = 0; batchStart < chunkCount; batchStart += maxConcurrent) {
          const batchEnd = Math.min(batchStart + maxConcurrent, chunkCount)
          const batchPromises = []

          for (let i = batchStart; i < batchEnd; i++) {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize - 1, fileSize - 1)

            const downloadChunk = async () => {
              const chunkHeaders = { ...headers, 'Range': `bytes=${start}-${end}` }
              const chunkFetchOptions = {
                headers: chunkHeaders,
                referrer: referrer,
                referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
              }
              const response = await fetch(url, chunkFetchOptions)

              if (!response.ok) {
                throw new Error(`分片下载失败: ${response.status}`)
              }

              const arrayBuffer = await response.arrayBuffer()
              chunks[i] = new Uint8Array(arrayBuffer)
              downloadedChunks.add(i)
              downloadedBytes += arrayBuffer.byteLength

              const elapsed = (Date.now() - startTime) / 1000
              const speed = downloadedBytes / elapsed
              const progress = Math.round((downloadedChunks.size / chunkCount) * 100)

              this.updateDownloadProgress(downloadKey, progress, speed)
            }

            batchPromises.push(downloadChunk())
          }

          await Promise.all(batchPromises)
        }

        this.updateDownloadProgress(downloadKey, 95, null, 'finalizing')

        const mergedArray = new Uint8Array(fileSize)
        let offset = 0
        for (const chunk of chunks) {
          if (chunk) {
            mergedArray.set(chunk, offset)
            offset += chunk.length
          }
        }

        const blob = new Blob([mergedArray])
        const timestamp = new Date().getTime().toString().slice(-8)
        let filename = `video_removed_${timestamp}.mp4`

        this.triggerDownload(blob, filename)
        this.updateDownloadProgress(downloadKey, 100, null, 'completed')

        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 2000)

      } catch (error) {
        console.error('多线程下载失败，回退到单线程:', error)
        return this.downloadVideoSingleThread(url, type, httpHeaders)
      }
    },

    // 单线程下载
    async downloadVideoSingleThread(url, type, httpHeaders = null) {
      const downloadKey = `${type}_${url}`

      try {
        const timestamp = new Date().getTime().toString().slice(-8)
        let filename = `video_removed_${timestamp}.mp4`

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 300000)

        let headers = {}
        let referrer = undefined

        if (httpHeaders) {
          headers = { ...httpHeaders }
          const refererKey = Object.keys(headers).find(k => k.toLowerCase() === 'referer')
          if (refererKey) {
            referrer = headers[refererKey]
          }
        } else {
          headers = {
            'Accept': '*/*',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          }
          referrer = this.getRefererFromUrl(url)
        }

        const fetchOptions = {
          method: 'GET',
          headers: headers,
          signal: controller.signal,
          referrer: referrer,
          referrerPolicy: referrer ? 'no-referrer' : 'unsafe-url'
        }

        const response = await fetch(url, fetchOptions)

        clearTimeout(timeoutId)

        if (!response.ok) {
          throw new Error(`下载失败: HTTP ${response.status}`)
        }

        const contentLength = response.headers.get('content-length')
        if (contentLength) {
          const fileSize = parseInt(contentLength, 10)
          const fileSizeMB = fileSize / (1024 * 1024)
          if (fileSizeMB > 100) {
            console.warn(`文件较大: ${fileSizeMB.toFixed(1)}MB`)
          }
        }

        this.updateDownloadProgress(downloadKey, 50, null, 'processing')
        const blob = await response.blob()
        this.updateDownloadProgress(downloadKey, 80, null, 'finalizing')
        this.triggerDownload(blob, filename)
        this.updateDownloadProgress(downloadKey, 100, null, 'completed')

        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 2000)

      } catch (error) {
        console.error('单线程下载失败:', error)
        this.downloadProgress[downloadKey] = { progress: 0, status: 'error' }
        
        if (error.name === 'AbortError') {
          showToast('下载超时')
        } else {
          showToast('下载失败')
        }

        setTimeout(() => {
          this.downloadingItems.delete(downloadKey)
          delete this.downloadProgress[downloadKey]
        }, 3000)
      }
    },
    
    async copyToClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
          showToast('复制成功')
        } else {
          showToast('复制失败，请手动复制')
        }
      } catch (err) {
        showToast('复制失败')
      }
    }
  }
}
</script>

<style scoped>
.tool-page .home-main {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
}

.tool-page .hero-section.tool-page-hero {
  padding: 28px;
}

.tool-page .tool-workbench--link {
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.tool-page .container,
.video-editor-container {
  width: 100%;
  max-width: none;
  margin: 0;
}

.video-editor-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 24px;
  box-shadow: 0 18px 45px rgba(50, 71, 117, 0.08);
}

.hidden-input {
  display: none;
}

.upload-area {
  width: 100%;
  min-height: 320px;
  border: 2px dashed rgba(79, 94, 122, 0.18);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: linear-gradient(180deg, rgba(247, 249, 252, 0.88), rgba(255, 255, 255, 0.92));
  transition: 0.2s ease;
}

.upload-area:hover {
  border-color: rgba(79, 70, 229, 0.32);
  background: rgba(79, 70, 229, 0.04);
}

.upload-area.is-drag-active {
  border-color: rgba(79, 70, 229, 0.48);
  background: rgba(79, 70, 229, 0.08);
  box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.12);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-icon {
  width: 52px;
  height: 52px;
  color: var(--brand);
}

.upload-text {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}

.upload-hint {
  font-size: 14px;
  color: var(--muted);
}

.editor-area {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.canvas-wrapper {
  position: relative;
  width: 100%;
  min-height: 320px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #0f172a;
  border-radius: 24px;
  overflow: hidden;
}

.editor-image {
  max-width: 100%;
  max-height: 60vh;
  display: block;
}

.mask-box {
  position: absolute;
  border: 2px solid #ff5500;
  background: rgba(255, 85, 0, 0.18);
  cursor: move;
  z-index: 10;
  touch-action: none;
}

.resize-handle {
  position: absolute;
  width: 16px;
  height: 16px;
  background: #ff5500;
  border: 2px solid #fff;
  border-radius: 50%;
  z-index: 11;
}

.handle-tl { top: -8px; left: -8px; cursor: nw-resize; }
.handle-tr { top: -8px; right: -8px; cursor: ne-resize; }
.handle-bl { bottom: -8px; left: -8px; cursor: sw-resize; }
.handle-br { bottom: -8px; right: -8px; cursor: se-resize; }

.toolbar {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px;
  background: rgba(247, 249, 252, 0.9);
  border: 1px solid rgba(223, 230, 242, 0.92);
  border-radius: 20px;
}

.tool-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.action-group {
  margin-left: auto;
}

.tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid rgba(223, 230, 242, 0.96);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 14px;
  cursor: pointer;
  font-weight: 600;
  color: var(--ink);
  transition: 0.2s ease;
}

.tool-btn svg {
  width: 18px;
  height: 18px;
}

.tool-btn:hover:not(:disabled),
.tool-btn.active {
  border-color: rgba(79, 70, 229, 0.28);
  color: var(--brand-deep);
  background: rgba(79, 70, 229, 0.06);
}

.tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tool-btn.primary {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, #5b6bff 0%, #2f80ed 100%);
}

.tool-btn.primary:hover:not(:disabled) {
  color: #fff;
}

.tool-btn.success {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, #16a34a 0%, #10b981 100%);
}

.tool-btn.secondary {
  background: rgba(248, 250, 252, 0.95);
}

.divider {
  width: 1px;
  height: 28px;
  background: rgba(223, 230, 242, 0.96);
}

.hint-text {
  margin: 16px 0 0;
  color: var(--muted);
  line-height: 1.7;
}

.tool-page .tool-info-section .container {
  width: 100%;
}

.tool-page .steps-grid,
.tool-page .features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  padding: 0;
}

.tool-page .step-card,
.tool-page .feature-card {
  padding: 24px;
  text-align: left;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(223, 230, 242, 0.92);
  border-radius: 24px;
  box-shadow: 0 14px 28px rgba(50, 71, 117, 0.06);
  transition: 0.2s ease;
}

.tool-page .step-card:hover,
.tool-page .feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 34px rgba(50, 71, 117, 0.1);
}

.step-number {
  width: 48px;
  height: 48px;
  margin: 0 0 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #5b6bff 0%, #2f80ed 100%);
}

.feature-icon {
  width: 52px;
  height: 52px;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  color: var(--brand);
  background: rgba(79, 70, 229, 0.1);
}

.feature-icon svg {
  width: 24px;
  height: 24px;
}

.step-title,
.feature-title {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  color: var(--ink);
}

.step-description,
.feature-description {
  margin: 0;
  color: var(--muted);
  line-height: 1.8;
}

.tool-page .learn-more {
  display: flex;
  justify-content: center;
}

.tool-page .section-title {
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .tool-page .hero-section.tool-page-hero {
    padding: 20px;
  }

  .video-editor-container {
    padding: 20px;
  }

  .toolbar {
    padding: 14px;
  }

  .action-group {
    margin-left: 0;
  }

  .divider {
    display: none;
  }
}
</style>
